import BulletList from '@tiptap/extension-bullet-list';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Placeholder from '@tiptap/extension-placeholder';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { marked } from 'marked';
import { useEffect, useImperativeHandle, forwardRef } from 'react';
import toast from 'react-hot-toast';
import { Markdown } from 'tiptap-markdown';

interface TiptapEditorProps {
  initialContent?: string;
  onChange?: (markdown: string) => void;
  placeholder?: string;
  editable?: boolean;
  className?: string;
  minHeight?: string;
  showBulletPoints?: boolean;
}

export interface TiptapEditorRef {
  copyContent: (format?: 'markdown' | 'html') => Promise<boolean>;
  getContent: (format?: 'markdown' | 'html') => string;
  getEditor: () => Editor | null;
  setContent: (content: string) => void;
}

export const formatMarkdownContent = (markdown: string): string => {
  return markdown
    .split('\n')
    .map((line) => {
      const match = line.match(/^([\t ]*)[-*]\s+(.*)/); // Detect indentation (tabs or spaces) before bullet
      if (match) {
        const indent = match[1]; // Capture leading spaces/tabs
        const level = indent.replace(/ {2}/g, '\t').replace(/\t/g, '\t').length; // Count indentation levels

        let bullet = '•'; // Default bullet for level 1
        if (level === 1) bullet = '◦'; // Level 2
        else if (level >= 2) bullet = '▪'; // Level 3+

        return `${'\t'.repeat(level)}${bullet} ${match[2].trim()}`; // Ensure exactly **one** space after bullet
      }
      return line.trim(); // Trim unnecessary spaces on other lines
    })
    .join('\n')
    .replace(/\*\*(.*?)\*\*/g, '*$1*') // Bold
    .replace(/__(.*?)__/g, '*$1*') // Bold alternative
    .replace(/\*(.*?)\*/g, '_$1_') // Italic
    .replace(/_(.*?)_/g, '_$1_') // Italic alternative
    .replace(/```([\s\S]*?)```/g, '```$1```') // Code block
    .replace(/`(.*?)`/g, '`$1`') // Inline code
    .replace(/\[(.*?)\]\((.*?)\)/g, '<$2|$1>');
};

const TiptapEditor = forwardRef<TiptapEditorRef, TiptapEditorProps>(
  (
    {
      initialContent = '',
      onChange,
      placeholder = 'Start typing...',
      editable = true,
      className = '',
      minHeight = '100px',
      showBulletPoints = false,
    },
    ref,
  ): JSX.Element => {
    const editor = useEditor({
      extensions: [
        StarterKit,
        Markdown,
        BulletList,
        ListItem,
        OrderedList,
        Placeholder.configure({
          placeholder,
          includeChildren: true,
          showOnlyWhenEditable: true,
          showOnlyCurrent: false,
          emptyEditorClass: 'is-editor-empty',
        }),
      ],
      content: initialContent ? marked.parse(initialContent) : '',
      editable,
      onUpdate: ({ editor: editorInstance }) => {
        if (onChange) {
          const markdown = (
            editorInstance.storage as {
              markdown: { getMarkdown: () => string };
            }
          ).markdown.getMarkdown();
          onChange(markdown);
        }
      },
      editorProps: {
        attributes: {
          placeholder,
        },
        handleKeyDown: (view, event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            return true;
          }
          return false;
        },
      },
    });
    useImperativeHandle(ref, () => ({
      copyContent: async (format: 'markdown' | 'html' = 'markdown') => {
        if (!editor) return false;

        const content: string =
          format === 'markdown'
            ? formatMarkdownContent(
                (
                  editor.storage as { markdown: { getMarkdown: () => string } }
                ).markdown.getMarkdown(),
              )
            : editor.getHTML();

        try {
          await navigator.clipboard.writeText(content);
          toast.success('Content copied to clipboard');
          return true;
        } catch (err) {
          toast.error('Failed to copy content');
          return false;
        }
      },
      getContent: (format: 'markdown' | 'html' = 'markdown') => {
        if (!editor) return '';
        return format === 'markdown'
          ? (
              editor.storage as { markdown: { getMarkdown: () => string } }
            ).markdown.getMarkdown()
          : editor.getHTML();
      },
      getEditor: () => editor,
      setContent: (content: string) => {
        if (editor) {
          editor.commands.setContent(content);
        }
      },
    }));

    useEffect(() => {
      if (editor) {
        editor.setEditable(editable);
      }
    }, [editor, editable]);

    useEffect(() => {
      if (editor) {
        const currentContent = (
          editor.storage as { markdown: { getMarkdown: () => string } }
        ).markdown.getMarkdown();

        if (currentContent !== initialContent) {
          editor.commands.setContent(
            initialContent?.trim() ? marked.parse(initialContent) : '',
          );
        }
        if (onChange) {
          onChange(initialContent);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, initialContent]);

    return (
      <div
        className={`text-ellipsis-tiptap outline-none w-full pr-3 ${className}`}
      >
        <style>{`
        .editor-content {
          font-family: 'Nunito', sans-serif;
          min-height: ${minHeight};
          width: 100%;
          max-width: 100%;
          overflow-wrap: break-word;
          word-wrap: break-word;
          word-break: break-word;
        }
        .is-editor-empty::before {
          content: attr(data-placeholder);
          color: #adb5bd;
          pointer-events: none;
          float: left;
          height: 0;
        }
        .ProseMirror {
          width: 100%;
          max-width: 100%;
          overflow-wrap: break-word;
          word-wrap: break-word;
          word-break: break-word;
        }
        ${
          showBulletPoints
            ? `
        .editor-content ul {
          outline: none;
          list-style-type: disc;
          padding-left: 20px;
          margin-bottom: 0.5em;
        }
        .editor-content ul ul {
          list-style-type: circle;
          padding-left: 20px;
        }
        .editor-content ul ul ul {
          list-style-type: square;
          padding-left: 20px;
        }
        .editor-content ol {
          list-style-type: decimal;
          padding-left: 20px;
          margin-bottom: 0.5em;
        }
        `
            : ''
        }
      `}</style>
        {editor && (
          <EditorContent
            editor={editor}
            className="m-2 outline-none editor-content"
          />
        )}
      </div>
    );
  },
);

TiptapEditor.displayName = 'TiptapEditor';

export default TiptapEditor;
